import React, { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Button,
    Typography,
    CircularProgress,
    Collapse,
    IconButton,
    Chip
} from '@mui/material';
import {
    Assessment as ProfileIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Info as InfoIcon
} from '@mui/icons-material';
import { functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { toast } from 'react-toastify';
import ClientProfileModal from './ClientProfileModal';

const ClientProfile = ({ clientId, clientIsActive }) => {
    const [isGenerating, setIsGenerating] = useState(false);
    const [showInfo, setShowInfo] = useState(false);
    const [isExpanded, setIsExpanded] = useState(true);
    const [profileData, setProfileData] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Clear stale profile when client changes
    React.useEffect(() => {
        setProfileData(null);
        setIsModalOpen(false);
    }, [clientId]);

    const handleGenerateProfile = async () => {
        if (!clientId || clientId === 'ALL') {
            toast.error('Please select a specific client to generate a profile.');
            return;
        }

        setIsGenerating(true);
        setIsModalOpen(true);
        setProfileData(null);

        try {
            const generateProfile = httpsCallable(functions, 'generateClientProfile', {
                timeout: 360000
            });
            const result = await generateProfile({ clientId });

            if (result.data.success) {
                setProfileData(result.data.profile);
                toast.success('Client profile generated successfully!');
            } else {
                throw new Error(result.data.message || 'Failed to generate profile');
            }
        } catch (error) {
            console.error('Error generating client profile:', error);
            toast.error(`Failed to generate profile: ${error.message}`);
            setIsModalOpen(false);
        } finally {
            setIsGenerating(false);
        }
    };

    const isDisabled = isGenerating || !clientId || clientId === 'ALL' || !clientIsActive;

    return (
        <>
            <Card
                elevation={2}
                sx={{
                    mb: 3,
                    border: '2px solid',
                    borderColor: 'grey.300',
                }}
            >
                <CardContent sx={{ pb: 2, '&:last-child': { pb: 2 } }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="h6" fontWeight={600} color={isDisabled && !isGenerating ? 'text.disabled' : 'inherit'}>
                                Client Profile
                            </Typography>
                        </Box>
                        <Box>
                            <IconButton
                                size="small"
                                onClick={() => setShowInfo(!showInfo)}
                                sx={{ mr: 1 }}
                            >
                                <InfoIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={() => setIsExpanded(!isExpanded)}
                            >
                                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                        </Box>
                    </Box>

                    <Collapse in={showInfo}>
                        <Box
                            sx={{
                                mb: 2,
                                p: 2,
                                backgroundColor: 'info.light',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'info.main'
                            }}
                        >
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>Generate a comprehensive profile for client alignment meetings.</strong>
                            </Typography>
                            <Typography variant="body2">
                                The Client Profile synthesizes all available data—company research, financial analysis,
                                strategic priorities, and active initiatives—into a single document you can review
                                with your client to ensure alignment. Use Strategic Pulse to update any non-financial
                                changes after your review.
                            </Typography>
                        </Box>
                    </Collapse>

                    <Collapse in={isExpanded}>
                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Comprehensive company overview combining research, financials, and strategic context.
                            </Typography>

                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={handleGenerateProfile}
                                    disabled={isDisabled}
                                    startIcon={isGenerating ? <CircularProgress size={16} color="inherit" /> : <ProfileIcon />}
                                    sx={{ py: 1.5, fontWeight: 600, fontSize: '1rem' }}
                                >
                                    {isGenerating ? 'Generating...' : 'Generate Profile'}
                                </Button>

                                {profileData && !isGenerating && (
                                    <Button
                                        variant="text"
                                        size="small"
                                        onClick={() => setIsModalOpen(true)}
                                        sx={{ whiteSpace: 'nowrap' }}
                                    >
                                        View Last Profile
                                    </Button>
                                )}
                            </Box>

                            {!clientIsActive && clientId !== 'ALL' && (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    Client is inactive. Profile generation is disabled.
                                </Typography>
                            )}

                            {clientId === 'ALL' && (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    Please select a specific client to generate a profile.
                                </Typography>
                            )}
                        </Box>
                    </Collapse>
                </CardContent>
            </Card>

            <ClientProfileModal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                profileData={profileData}
                isLoading={isGenerating}
            />
        </>
    );
};

export default ClientProfile;
